import React, { useState } from 'react';
import { CheckCheck } from 'lucide-react';
import LineRow from './LineRow';
import ConfirmDialog from './ConfirmDialog';

/** Every line with its match status, plus "Confirm all suggestions". Every bought line maps to an inventory
 * item: a likely match is confirmed here (or another item found or created); a line nothing resembles was
 * already added to the inventory by the pipeline. */
export default function InvoiceLines({ views, itemNames, onResolveMany, busy, progress }) {
  const [filter, setFilter] = useState('action');
  const [confirmAll, setConfirmAll] = useState(false);

  const needing = views.filter((v) => v.status === 'needs_match');
  const withSuggestion = needing.filter((v) => v.suggestions.length > 0);
  const created = views.filter((v) => v.line.new_item);
  const action = views.filter((v) => v.status === 'needs_match' || v.status === 'needs_category');
  const shown = filter === 'action' && action.length ? action : views;

  const run = (jobs) => onResolveMany(jobs);
  const confirmJob = (v, itemId) => ({ task: v.skuTask, body: { item_id: itemId, raw_desc: v.raw } });
  const categoryJob = (v, c) => ({ task: v.glTask, body: { gl_category: c } });

  return (
    <section className="space-y-4" data-testid="invoice-lines">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-ink">Lines <span className="text-ink-subtle text-base">({views.length})</span></h2>
          <p className="text-sm text-ink-muted" data-testid="lines-counts">
            {needing.length} need a match · {withSuggestion.length} with a suggestion
            {created.length > 0 && ` · ${created.length} new ${created.length === 1 ? 'item' : 'items'} added to the inventory`}
          </p>
        </div>
        <div role="group" aria-label="Show lines" className="flex rounded-full border border-surface-border p-1 bg-surface">
          {[['action', 'Needs action'], ['all', 'All lines']].map(([id, label]) => (
            <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)} data-testid={`lines-filter-${id}`}
              className={`min-h-[40px] px-4 rounded-full text-sm ${filter === id ? 'bg-primary-500 text-surface' : 'text-ink-muted'}`}>{label}</button>
          ))}
        </div>
      </div>

      {needing.length > 0 && (
        <div className="sticky top-16 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-surface-border bg-surface/95 backdrop-blur p-3" data-testid="lines-bulk-bar">
          <button type="button" disabled={busy || !withSuggestion.length} onClick={() => setConfirmAll(true)} data-testid="lines-confirm-all-suggestions"
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-primary-500 text-surface text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
            <CheckCheck className="w-4 h-4" /> Confirm all suggestions ({withSuggestion.length})
          </button>
          {progress && <span className="text-sm text-ink-muted" data-testid="lines-progress" aria-live="polite">{progress}</span>}
        </div>
      )}

      <ul className="space-y-3">
        {shown.map((v) => (
          <LineRow key={v.index} view={v} busy={busy}
            itemName={itemNames[v.line.item_id]}
            onConfirm={(lv, itemId) => run([confirmJob(lv, itemId)])}
            onCategory={(lv, c) => run([categoryJob(lv, c)])} />
        ))}
      </ul>

      <ConfirmDialog open={confirmAll} title={`Confirm ${withSuggestion.length} suggestions?`} testid="confirm-all-dialog"
        confirmLabel="Confirm all" onCancel={() => setConfirmAll(false)}
        onConfirm={async () => { setConfirmAll(false); await run(withSuggestion.map((v) => confirmJob(v, v.suggestions[0].item_id))); }}>
        <p>Each line is matched to its first suggestion. Lines without a suggestion stay as they are.</p>
        <ul className="max-h-64 overflow-auto divide-y divide-surface-border border border-surface-border rounded-lg" data-testid="confirm-all-list">
          {withSuggestion.map((v) => (
            <li key={v.index} className="px-3 py-2 flex justify-between gap-3">
              <span className="text-ink truncate">{v.raw}</span>
              <span className="text-primary-700 shrink-0">→ {v.suggestions[0].name}</span>
            </li>
          ))}
        </ul>
      </ConfirmDialog>
    </section>
  );
}
